import { tachoItemsPageSize } from 'pages/employee-reviews/complete/employeeReviewCompleteHelpers';
import { isSameText } from './employeeReviewEditHelpers';

export const tachoEditItemStatuses = Object.freeze({
    reviewed: "Reviewed",
    toBeReturned: "ToBeReturned",
    returned: "Returned"
});

// The Wizard unmounts a step as soon as the manager moves off it, so the corrections made so far,
// the item open in the dialog and the table's page all live in the page's topic state. records
// holds each corrected reason and action by the item's review item id, and returns the items
// marked to be returned to outstanding.
export function createTachoInfringementsEditState() {
    return {
        items: [],
        clearedItems: [],
        itemsClearedBefore: null,
        loadedComment: "",
        comment: "",
        records: {},
        returns: {},
        debrief: null,
        pendingReturnId: null,
        pageNumber: 1,
        pageSize: tachoItemsPageSize
    };
}

// Cleared items were never reviewed, so they are shown apart from the table and cannot be changed.
export function applyLoadedTachoInfringements(topicEntry) {
    const items = topicEntry?.items ?? [];

    return {
        items: items.filter((item) => item.cleared !== true),
        clearedItems: items.filter((item) => item.cleared === true),
        itemsClearedBefore: topicEntry?.itemsClearedBefore ?? null,
        loadedComment: topicEntry?.comment ?? "",
        comment: topicEntry?.comment ?? ""
    };
}

// As the API reads the text sent: trimmed, with nothing entered held as null.
function normaliseText(value) {
    const trimmed = (value ?? "").trim();
    return trimmed === "" ? null : trimmed;
}

export function getTachoEditItemStatus(state, item) {
    if (item.returnedToOutstanding === true) {
        return tachoEditItemStatuses.returned;
    }

    if (state?.returns?.[item.reviewItemId] === true) {
        return tachoEditItemStatuses.toBeReturned;
    }

    return tachoEditItemStatuses.reviewed;
}

// The reason, action and emergency flag an item holds now: as corrected on this page, or as the
// review recorded them.
export function getCurrentTachoAnswers(state, item) {
    const record = state?.records?.[item.reviewItemId];

    return {
        reasonForFault: record?.reasonForFault ?? item.reasonForFault ?? "",
        actionTaken: record?.actionTaken ?? item.actionTaken ?? "",
        emergency: record?.emergency ?? item.emergency === true
    };
}

// The items the edit changes: a return carries nothing else, and a correction is sent only when
// its reason, action or emergency flag differs from what the review recorded, the text by the API's
// own rule. A correction always carries the reason and action as they stand, and carries the
// emergency flag only when it changed, because an absent flag leaves the recorded one - which may
// be unknown - as it is.
export function getTachoItemEdits(state) {
    return (state?.items ?? []).flatMap((item) => {
        if (getTachoEditItemStatus(state, item) === tachoEditItemStatuses.toBeReturned) {
            return [{ reviewItemId: item.reviewItemId, returnToOutstanding: true }];
        }

        const record = state?.records?.[item.reviewItemId];

        if (record == null) {
            return [];
        }

        const actionTaken = normaliseText(record.actionTaken);
        const reasonForFault = normaliseText(record.reasonForFault);

        const emergencyChanged = record.emergency != null && record.emergency !== (item.emergency === true);

        if (isSameText(actionTaken, item.actionTaken) && isSameText(reasonForFault, item.reasonForFault) && !emergencyChanged) {
            return [];
        }

        const edit = { reviewItemId: item.reviewItemId, actionTaken, reasonForFault };

        if (emergencyChanged) {
            edit.emergency = record.emergency;
        }

        return [edit];
    });
}

export function isTachoCommentChanged(state) {
    return !isSameText(state?.comment, state?.loadedComment);
}

// A correction open in the dialog counts as unsaved work once it differs from what the item holds.
function hasDebriefDraftChanges(state) {
    const debrief = state?.debrief ?? null;
    const item = (state?.items ?? []).find((candidate) => candidate.reviewItemId === debrief?.reviewItemId);

    if (debrief == null || item == null) {
        return false;
    }

    const current = getCurrentTachoAnswers(state, item);

    return !isSameText(debrief.reasonForFault, current.reasonForFault)
        || !isSameText(debrief.actionTaken, current.actionTaken)
        || (debrief.emergency === true) !== current.emergency;
}

export function isTachoInfringementsEditDirty(state) {
    return isTachoCommentChanged(state) || getTachoItemEdits(state).length > 0 || hasDebriefDraftChanges(state);
}

export function getTachoInfringementsEditGating(state) {
    const canProceed = state?.debrief == null;

    return {
        canProceed,
        cantProceedTooltip: canProceed ? null : "Save or cancel the infringement you are editing before continuing."
    };
}
